import { questExplainerBlocks, questMetaDescriptionText, questPageTitle, type QuestSeoFacts } from "../../shared/quest-seo.ts";
import { locale } from "../i18n/index.ts";
import { el } from "./dom.ts";
import type { QuestDetail } from "./types.ts";
import { questSeoFactsFromDetail } from "./quest-auto-summary.ts";

function toSeoLocale(): "en" | "ko" {
  return locale() === "ko" ? "ko" : "en";
}

/** Quest-type explainer (route vs spot); collapsible but always present in the DOM. */
export function mountRouteQuestExplainer(q: QuestDetail): HTMLElement {
  const facts = questSeoFactsFromDetail(q);
  const copy = questExplainerBlocks(
    {
      startFixed: facts?.startFixed ?? Boolean(q.start_fixed),
      timeLimitS: facts?.timeLimitS ?? q.time_limit_s ?? null,
      questType: facts?.questType ?? (q.quest_type === "spot_trace" ? "spot_trace" : "route_trace"),
    },
    toSeoLocale(),
  );
  const section = el("section", "qp-explainer");
  const details = document.createElement("details");
  details.className = "qp-explainer-details";
  details.open = false;
  const summary = document.createElement("summary");
  summary.className = "qp-explainer-summary";
  summary.textContent = copy.title;
  details.append(summary);
  const body = el("div", "qp-explainer-body");
  body.append(
    el("p", "qp-explainer-p", copy.what),
    el("p", "qp-explainer-p", copy.how),
    el("p", "qp-explainer-p", copy.tips),
  );
  details.append(body);
  section.append(details);
  return section;
}

export function questPageDocumentTitle(title: string): string {
  return questPageTitle(title, toSeoLocale());
}

export function questMetaDescription(description: string | null | undefined, autoSummary: string | null): string {
  const text = description?.trim() || autoSummary?.trim() || "";
  if (!text) return "";
  if (text.length <= 160) return text;
  return `${text.slice(0, 159).trimEnd()}…`;
}

export function questMetaDescriptionFromQuest(q: QuestDetail): string {
  const facts = questSeoFactsFromDetail(q);
  if (!facts) return questMetaDescription(q.description, null);
  return questMetaDescriptionText(facts, toSeoLocale());
}

export function applyQuestPageHead(opts: { title: string; description: string }): void {
  document.title = questPageDocumentTitle(opts.title);
  const content = opts.description.trim();
  let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!content) {
    meta?.remove();
    return;
  }
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.append(meta);
  }
  meta.content = content;
}

export type { QuestSeoFacts };
